import React from "react";
import { isMyCarLegToParkAndRide } from "../../../tools/parkAndRideOsmTags.tools";
import {
    extractRouteTypeFranceCodeFromTripId,
    resolveRouteTypeFranceBadgeForLine,
} from "../../../tools/routeTypeFranceBadge.tools";
import { getTransportModeGroupForItineraryLeg, TransportModeGroup } from "../../../tools/TransportModeGroupsTools";
import { type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { getItineraryDetailsLegPillColor, getItineraryDetailsLegPillLabel, hasNonEmptyText, isSimpleMobilityLegMode } from "./itineraryDetails.tools";

interface ItineraryDetailsLegPillProps { "leg": ItineraryLegDTO }

/** Shared chrome for filled route / direction pills in itinerary leg title rows. */
const ITINERARY_DETAILS_LEG_PILL_CLASS_NAME =
    "text-xs font-semibold mr-2 rounded-md rounded-bl-none text-white px-2";

const ItineraryDetailsLegPill: React.FC<ItineraryDetailsLegPillProps> = ( { leg } ) => {
    const modeGroup = getTransportModeGroupForItineraryLeg( leg );

    if ( isMyCarLegToParkAndRide( leg ) ) {
        return (
            <span
                aria-hidden={true}
                className={ITINERARY_DETAILS_LEG_PILL_CLASS_NAME}
                style={{ "backgroundColor": getItineraryDetailsLegPillColor( leg ) }}
            >
                →
            </span>
        );
    }

    if ( isSimpleMobilityLegMode( leg.mode ) ) {
        return null;
    }

    const franceBadge = resolveRouteTypeFranceBadgeForLine(
        extractRouteTypeFranceCodeFromTripId( leg.providerId ),
        leg.route?.shortName,
    );

    /*
     * SNCF product legs render the product icon in an outlined chip: the border takes the
     * timeline line color/thickness so the pill reads as a wrap of the connector, not a stain.
     */
    if ( franceBadge !== null ) {
        return (
            <span
                className={`${ ITINERARY_DETAILS_LEG_PILL_CLASS_NAME } inline-flex items-center justify-center border-[3px] py-[1px]`}
                style={{
                    "backgroundColor": "#ffffff",
                    "borderColor":     getItineraryDetailsLegPillColor( leg ),
                }}
            >
                <img
                    alt={franceBadge.label}
                    className="block h-3.5 w-auto object-contain"
                    src={franceBadge.icon}
                />
            </span>
        );
    }

    const pillLabel = getItineraryDetailsLegPillLabel( leg );

    if ( hasNonEmptyText( pillLabel ) ) {
        return (
            <span
                className={ITINERARY_DETAILS_LEG_PILL_CLASS_NAME}
                style={{ "backgroundColor": getItineraryDetailsLegPillColor( leg ) }}
            >
                {pillLabel}
            </span>
        );
    } else if ( [ TransportModeGroup.RENTAL, TransportModeGroup.BICYCLE, TransportModeGroup.BLABLACAR, TransportModeGroup.BLABLADAILY ].includes( modeGroup ) ) {
        return (
            <div
                className="mr-2 w-[3px] self-stretch"
                style={{ "backgroundColor": getItineraryDetailsLegPillColor( leg ) }}
            />
        );
    } else {
        return (
            <div
                className="mr-2 w-[3px] self-stretch"
                style={{ "backgroundColor": getItineraryDetailsLegPillColor( leg ) }}
            />
        );
    }
};

export default ItineraryDetailsLegPill;
